import React, { useReducer } from "react";
import {
  CHECKEDIN,
  GET_PARTICIPANT,
  GET_PARTICIPANTS,
  SET_LOADING,
  SET_PARTICIPANT_PAGINATION,
  SET_PARTICIPANTS,
} from "../types";
import ParticipantContext from "./participantContext";
import ParticipantReducer from "./participantReducer";
import storage from "../../helpers/storage";
import axiosInstance from "../../services/axios.service";

const ParticipantState = (props) => {
  const initialState = {
    participants: [],
    participant: {},
    checkedIn: [],
    participantPagination: {},
    loading: false,
  };

  const [state, dispatch] = useReducer(ParticipantReducer, initialState);

  const setLoading = () => {
    dispatch({
      type: SET_LOADING,
    });
  };

  const getParticipants = async (eventId, limit = 30, page = 1) => {
    const populate = `_populate[]=resourceGroups&_populate[]=room&_populate[]=servicePoint&_populate[]=accomodation&_populate=event&_populate[]=center&_orderBy=lastName&_order=asc`;
    const q = `event=${eventId}&_limit=${limit}&_page=${page}&${populate}`;
    setLoading();

    try {
      const resp = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_URL}/participants?${q}`,
        storage.getConfigWithBearer()
      );
      let data = [];
      resp.data.data.docs.filter((c) => {
        if (c.checkedIn === false) {
          data.push(c);
        }
      });

      const paginate = {
        page: resp.data.data.page,
        hasNextPage: resp.data.data.hasNextPage,
        hasPreviousPage: resp.data.data.hasPreviousPage,
        nextPage: resp.data.data.nextPage,
        previousPage: resp.data.data.previousPage,
        limit: resp.data.data.limit,
        totalDocs: resp.data.data.totalDocs,
        totalPages: resp.data.data.totalPages,
      };

      dispatch({
        type: SET_PARTICIPANT_PAGINATION,
        payload: paginate,
      });

      dispatch({
        type: CHECKEDIN,
        payload: data,
      });
      dispatch({
        type: GET_PARTICIPANTS,
        payload: eventId === null ? [] : resp.data.data.docs,
      });
    } catch (error) {
      console.error("Error in getParticipants:", error);
    }
  };

  const getParticipant = async (participantId) => {
    const populate = `_populate=event&_populate[]=resourceGroups&_populate[]=room&_populate[]=servicePoint&_populate[]=accomodation&_populate[]=center&_orderBy=lastName&_order=asc`;

    const q = `${populate}`;

    setLoading();

    try {
      const resp = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_URL}/participants/${participantId}?${q}`,
        storage.getConfigWithBearer()
      );

      dispatch({
        type: GET_PARTICIPANT,
        payload: resp.data.data,
      });
    } catch (error) {
      console.error("Error in getParticipant:", error);
    }
  };

  const setParticipants = (data) => {
    dispatch({
      type: SET_PARTICIPANTS,
      payload: data,
    });
  };

  // POST /participants/:id/check-in with an optional { room, resourceGroups }
  // body. Omitted fields mean auto-select. Note it is a *room* that gets
  // assigned, not an accommodation: an accommodation is the building, a room
  // is the thing with beds in it.
  /**
   * Correct a participant's details from the desk.
   *
   * Its own grant on the API — `edit_participant:manage` — because fixing
   * a spelling should not require the power to delete registrations.
   */
  /**
   * Set a password for somebody who cannot receive email.
   *
   * Returned once and never stored, so the caller must show it before it
   * is lost. Whatever password the account had stops working the moment
   * this succeeds.
   */
  const resetUserPassword = async (userId) => {
    try {
      const resp = await axiosInstance.post(
        `${process.env.REACT_APP_BASE_URL}/admins/users/${userId}/reset-password`,
        {},
        storage.getConfigWithBearer()
      );
      return { success: true, data: resp.data.data ?? resp.data };
    } catch (error) {
      return { success: false, error };
    }
  };

  /**
   * Money taken at the desk. The API asks Paystack first, so someone who
   * already paid online comes back `alreadyPaid` rather than charged twice.
   */
  const recordPayment = async (participantId, payment) => {
    try {
      const resp = await axiosInstance.post(
        `${process.env.REACT_APP_BASE_URL}/participants/${participantId}/payments`,
        payment,
        storage.getConfigWithBearer()
      );
      return { success: true, data: resp.data.data ?? resp.data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const updateParticipant = async (participantId, changes) => {
    try {
      const resp = await axiosInstance.patch(
        `${process.env.REACT_APP_BASE_URL}/participants/${participantId}`,
        changes,
        storage.getConfigWithBearer()
      );
      dispatch({ type: GET_PARTICIPANT, payload: resp.data.data });
      return { success: true, data: resp.data.data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const checkInParticipant = async (participantId, { room, resourceGroups } = {}) => {
    const payload = {};
    if (room) payload.room = room;
    if (resourceGroups && resourceGroups.length > 0) payload.resourceGroups = resourceGroups;

    setLoading();
    try {
      const resp = await axiosInstance.post(
        `${process.env.REACT_APP_BASE_URL}/participants/${participantId}/check-in`,
        payload,
        storage.getConfigWithBearer()
      );

      dispatch({
        type: GET_PARTICIPANT,
        payload: resp.data.data.participant,
      });

      // `assignment` reports what could and could not be allocated — a
      // participant is still checked in when the event is full, and the
      // desk needs to be told which it was.
      return {
        success: true,
        data: resp.data.data.participant,
        assignment: resp.data.data.assignment,
        alreadyCheckedIn: resp.data.data.alreadyCheckedIn,
        // Who came with them and is still not marked present. A family is
        // checked in one name at a time, so the children are the ones who
        // get missed.
        alsoRegistered: resp.data.data.alsoRegistered ?? [],
      };
    } catch (error) {
      console.error("Error in checkInParticipant:", error);
      return { success: false, error };
    }
  };

  return (
    <ParticipantContext.Provider
      value={{
        participants: state.participants,
        participant: state.participant,
        checkedIn: state.checkedIn,
        participantPagination: state.participantPagination,
        loading: state.loading,
        getParticipants,
        getParticipant,
        setParticipants,
        checkInParticipant,
        updateParticipant,
        resetUserPassword,
        recordPayment,
      }}
    >
      {props.children}
    </ParticipantContext.Provider>
  );
};

export default ParticipantState;
